import React from 'react';
import { tv } from 'tailwind-variants';
import type {
  FieldGroup as FieldGroupType,
  FieldConfig,
  FormValues,
  FormErrors,
  FormTouched,
  FormInstance,
  StepConfig,
  CustomErrorMessages,
} from '../../types';
import { evaluateConditions } from '../../utils/conditions';
import { renderFieldComponent } from '../../RenderFieldComponent';

interface FieldGroupProps {
  fieldGroup: FieldGroupType;
  values: FormValues;
  errors: FormErrors;
  touched: FormTouched;
  handleChange: (field: FieldConfig, rawValue: unknown) => void;
  setTouched: React.Dispatch<React.SetStateAction<FormTouched>>;
  completedForms?: Record<string, FormInstance[]>;
  stepsById?: Record<string, StepConfig>;
  onEditInstance?: (instanceId: string, groupKey: string) => void;
  // New props for radio inline cards
  setValues?: React.Dispatch<React.SetStateAction<FormValues>>;
  setCompletedForms?: React.Dispatch<React.SetStateAction<Record<string, FormInstance[]>>>;
  setErrors?: React.Dispatch<React.SetStateAction<FormErrors>>;
  localStorageKey?: string; // Optional localStorage key to load estate values from
  editingInstanceId?: string | null; // Optional instance ID being edited (for progress bar calculation)
  onNavigate?: (stepId: string) => void; // Navigation function for add new button
  customErrorMessages?: CustomErrorMessages;
}

type FieldChunk =
  | { type: 'single'; fields: FieldConfig[] }
  | { type: 'group'; groupKey: string; fields: FieldConfig[] };

function preserveFieldListOrder(fields: FieldConfig[]): FieldChunk[] {
  const fieldList: FieldChunk[] = [];

  for (const field of fields) {
    const groupKey = field.group; // undefined => individual
    const last = fieldList[fieldList.length - 1];

    if (!groupKey) {
      // individual field: keep as its own chunk (or merge with previous single chunk if you want)
      if (last?.type === 'single') {
        last.fields.push(field);
      } else {
        fieldList.push({ type: 'single', fields: [field] });
      }
      continue;
    }

    // grouped field: merge only if previous chunk is same groupKey
    if (last?.type === 'group' && last.groupKey === groupKey) {
      last.fields.push(field);
    } else {
      fieldList.push({ type: 'group', groupKey, fields: [field] });
    }
  }

  return fieldList;
}

export const FieldGroup: React.FC<FieldGroupProps> = ({
  fieldGroup,
  values,
  errors,
  touched,
  handleChange,
  setTouched,
  completedForms,
  stepsById,
  onEditInstance,
  setValues,
  setCompletedForms,
  setErrors,
  localStorageKey,
  editingInstanceId,
  onNavigate,
  customErrorMessages,
}) => {
  if (!evaluateConditions(fieldGroup.showIf, values)) {
    return null;
  }

  const fieldList = preserveFieldListOrder(fieldGroup.fields);

  const { container, groupContainer } = TAILWIND_VARIANTS();
  const baseContainerClasses = container();
  const containerClassName = fieldGroup.className
    ? `${baseContainerClasses} ${fieldGroup.className}`.trim()
    : baseContainerClasses;
  const baseGroupClasses = groupContainer();
  const groupContainerClassName = fieldGroup.groupClassName
    ? `${baseGroupClasses} ${fieldGroup.groupClassName}`.trim()
    : baseGroupClasses;

  return (
    <div className={containerClassName}>
      {fieldList.map((item, index) => {
        const fieldElements = item.fields.map((field) =>
          renderFieldComponent(
            field,
            values,
            errors,
            touched,
            handleChange,
            setTouched,
            completedForms,
            stepsById,
            onEditInstance,
            setValues,
            setCompletedForms,
            setErrors,
            localStorageKey,
            editingInstanceId,
            onNavigate,
            customErrorMessages
          )
        );

        if (item.type === 'group') {
          // key includes index to allow same groupKey to appear in multiple places
          return (
            <div key={`${item.groupKey}-${index}`} className={groupContainerClassName}>
              {fieldElements}
            </div>
          );
        }

        return <React.Fragment key={`single-${index}`}>{fieldElements}</React.Fragment>;
      })}
    </div>
  );
};

const TAILWIND_VARIANTS = tv({
  slots: {
    container: ['field-group', 'flex', 'flex-col', 'gap-component-section-container-gutter'],
    groupContainer: [
      'grid',
      'grid-cols-1',
      'md:grid-cols-3',
      'gap-general-spacing-gutter',
      'w-full',
    ],
  },
});
